import {
    Alarm,
    AlarmEmailDTO,
    Application,
    ApplicationUserViewModel,
    ApprovalSettings,
    Category,
    ConnectionPoint,
    ConnectionPointTestResult,
    Dashboard,
    DigitalCorrespondanceTable,
    DigitalSet,
    DigitalSetWithCode,
    GlobalNotification,
    Group,
    HistorianStorageConfiguration,
    IAction,
    IApiInformation,
    IApprovalStrategy,
    Identifier,
    IGroupTreeNode,
    ITask,
    ITrigger,
    IValueViewModel,
    LayoutState,
    MailingJobDefinitionDto,
    ObjectAccessRights,
    UserGroups,
    ObjectUser,  
    OnlineUserDto,
    Qualifier,
    State,
    TagDefinition,
    TagDefinitionDTO,
    Template,
    Ticket,
    TicketStatus,
    TicketType,
    TriggerAction,
    Unit,
    ValidationPageConfiguration,
    VariableSubType,
    VariableType,
} from '../models';
import { IAuthentication } from '../services';

export type GetStore = () => Store;
export type Dispatcher<T> = (dispatch: Dispatch<T>, getStore: GetStore) => angular.IPromise<T>;
export type Dispatch<T> = (ev: DispatchEvent<T>) => void;
export type DispatchEvent<T> = { type: string; payload?: T };

export interface IDispatcherService<T> {
    getDispatcher(dispatch: Function): IDispatcher<T>;
}

export interface IDispatcher<T> {
    dispatcher: T;
}

export interface IStoreItem {
    isLoaded: boolean;
    isLoading: boolean;
    hasError: boolean;
}

export class StoreItem<T> implements IStoreItem {
    dictionary: { [guid: string]: T } = {};
    isLoading = false;
    isLoaded = false;
    hasError = false;
    error: any = null;
    isDirty?: boolean;
}

export class StoreNative<T> implements IStoreItem {
    value: T = null;
    isLoading = false;
    isLoaded = false;
    hasError = false;
}

export class StoreItemId<T> extends StoreItem<T> {
    dictionary: { [id: number]: T } = {};
}

export declare type TriggerActionStoreItem = IStoreItem & { list: TriggerAction[] };

export interface Store {
    actions: StoreItem<IAction>;
    alarmCategories: StoreItem<string>;
    alarmEmails: StoreItem<AlarmEmailDTO>;
    alarmTemplates: StoreItem<Alarm[]>;
    alarms: StoreItem<Alarm[]>;
    apiInformation: StoreNative<IApiInformation>;
    application: StoreNative<Application>;
    approvalSettings: StoreItem<ApprovalSettings>;
    approvalStrategies: StoreItem<IApprovalStrategy>;
    authentication: StoreNative<IAuthentication>;
    categories: StoreItem<Category>;
    connectionPoints: StoreItem<ConnectionPoint>;
    connectionPointsStatuses: StoreItem<ConnectionPointTestResult>;
    dashboardUsers: StoreItem<ObjectUser>;
    dashboards: StoreItem<Dashboard>;
    digitalCorrespondanceTables: StoreItem<DigitalCorrespondanceTable>;
    digitalSetDictionary: StoreItem<DigitalSetWithCode>;
    digitalSets: StoreItem<DigitalSet>;
    globalNotifications: StoreItem<GlobalNotification>;
    groups: StoreItem<Group>;
    historianStorageConfigurations: StoreItem<HistorianStorageConfiguration>;
    identifiers: StoreItem<Identifier>;
    layout: StoreNative<LayoutState>;
    mailingJobs: StoreItem<MailingJobDefinitionDto>;
    objectAccessRights: StoreItem<ObjectAccessRights>;
    onlineUsers: StoreItem<OnlineUserDto>;
    qualifiers: StoreItem<Qualifier>;
    selectedGroup: IGroupTreeNode;
    states: StoreItem<State>;
    tags: StoreItem<TagDefinition>;
    tagsDto: StoreItem<TagDefinitionDTO>;
    tasks: StoreItem<ITask>;
    templates: StoreItem<Template>;
    ticketStatuses: StoreItemId<TicketStatus>;
    ticketTypes: StoreItemId<TicketType>;
    tickets: StoreItemId<Ticket>;
    triggerActions: TriggerActionStoreItem;
    triggers: StoreItem<ITrigger>;
    units: StoreItem<Unit>;
    userGroups: StoreItem<UserGroups>;
    users: StoreItem<ApplicationUserViewModel>;
    /**
     * The validation page configurations (by type). Widgets configurations are deserialized.
     */
    validationPageConfigurations: StoreItem<ValidationPageConfiguration>;
    valueViewModels: StoreItem<IValueViewModel>;
    variablesTypes: StoreItem<VariableType>;
    variableSubTypes: StoreItem<VariableSubType>;
}

export function copy<T>(src: T): T {
    return Object.assign({}, src);
}